<template>
  <!-- Input invisible para capturar scanner -->
  <input
    ref="scannerTrap"
    v-model="hiddenScannerInput"
    @input="handleScannerInput"
    @compositionstart="onCompositionStart"
    @compositionend="onCompositionEnd"
    style="position: absolute; left: -9999px; opacity: 0; z-index: -1"
    autocomplete="off"
  />
</template>

<script>
export default {
  name: "InvoiceScanner",
  
  data() {
    return {
      // Scanner state
      isScanning: false,
      scanTimeout: null,
      barcode: "",
      hiddenScannerInput: "",
      isComposing: false,
      compositionData: "",
      focusInterval: null,
    };
  },

  mounted() {
    // Setup scanner trap and keyboard listeners
    this.setupScannerTrap();
    window.addEventListener("keypress", this.handleKeyPress);
  },

  beforeDestroy() {
    // Cleanup
    window.removeEventListener("keypress", this.handleKeyPress);
    if (this.focusInterval) {
      clearInterval(this.focusInterval);
    }
    if (this.scanTimeout) {
      clearTimeout(this.scanTimeout);
    }
  },

  methods: {
    /**
     * Configurar el scanner trap para mantener focus
     */
    setupScannerTrap() {
      // Mantener focus en el input invisible cuando no hay interacción
      document.addEventListener("click", (e) => {
        // Si el click no es en un input, select, textarea o button
        const activeElements = ["INPUT", "SELECT", "TEXTAREA", "BUTTON"];
        if (
          !activeElements.includes(e.target.tagName) &&
          !e.target.closest(".v-dialog")
        ) {
          setTimeout(() => {
            if (this.$refs.scannerTrap) {
              this.$refs.scannerTrap.focus();
            }
          }, 100);
        }
      });

      // Focus inicial
      setTimeout(() => {
        if (this.$refs.scannerTrap) {
          this.$refs.scannerTrap.focus();
        }
      }, 500);
    },

    /**
     * Manejar input del scanner - detecta códigos escaneados
     */
    handleScannerInput(event) {
      // Detectar si es entrada de scanner por el tipo de evento
      if (
        event.inputType === "insertCompositionText" ||
        (event.data && event.data.length > 5)
      ) {
        // Es probable que sea un scanner
        return;
      }

      // Para entrada normal, procesar cuando se detecte Enter
      if (
        this.hiddenScannerInput.includes("\n") ||
        this.hiddenScannerInput.includes("\r")
      ) {
        const code = this.hiddenScannerInput.replace(/[\n\r]/g, "");
        if (code.length > 0) {
          this.processScannerCode(code);
        }
        this.hiddenScannerInput = "";
      }
    },

    /**
     * Manejo de composición de texto (para scanners que usan IME)
     */
    onCompositionStart() {
      this.isComposing = true;
      this.compositionData = "";
    },

    onCompositionEnd(event) {
      this.isComposing = false;
      // Los scanners suelen enviar todo el código en un evento de composición
      if (event.data && event.data.length >= 8) {
        this.processScannerCode(event.data);
        this.hiddenScannerInput = "";
      }
    },

    /**
     * Procesar código escaneado - validar y emitir evento
     */
    processScannerCode(code) {
      // Limpiar el código de caracteres no deseados
      const cleanCode = code.trim();

      // Verificar que sea un código válido
      if (cleanCode.length >= 8 && /^[a-zA-Z0-9]+$/.test(cleanCode)) {
        // Emitir evento al padre con el código
        this.$emit('code-scanned', cleanCode);

        // Mostrar feedback visual
        this.$emit('show-message', {
          text: `Código escaneado: ${cleanCode}`,
          color: 'info',
          timeout: 1500
        });
      } else {
        // Código inválido
        this.$emit('show-message', {
          text: 'Código de barras inválido',
          color: 'warning',
          timeout: 2000
        });
      }
    },

    /**
     * Manejo de teclado - método backup para scanners especiales
     */
    handleKeyPress(event) {
      // Mantener compatibilidad con el método anterior
      // pero ahora como backup para teclados especiales
      
      // Si hay un input activo, no interferir
      if (document.activeElement && 
          ['INPUT', 'SELECT', 'TEXTAREA'].includes(document.activeElement.tagName)) {
        return;
      }

      if (!this.isScanning) {
        this.isScanning = true;
        this.barcode = "";
      }

      clearTimeout(this.scanTimeout);

      if (event.key === "Enter") {
        if (this.barcode.length >= 8) {
          this.processScannerCode(this.barcode);
        }
        this.barcode = "";
        this.isScanning = false;
        return;
      }

      // Acumular caracteres
      this.barcode += event.key;

      // Timeout para resetear si no es un scanner continuo
      this.scanTimeout = setTimeout(() => {
        this.isScanning = false;
        this.barcode = "";
      }, 100);
    },

    /**
     * Método público para dar focus al scanner
     */
    focusScanner() {
      if (this.$refs.scannerTrap) {
        this.$refs.scannerTrap.focus();
      }
    },

    /**
     * Método público para resetear el estado del scanner
     */
    resetScanner() {
      this.hiddenScannerInput = "";
      this.barcode = "";
      this.isScanning = false;
      this.isComposing = false;
      
      if (this.scanTimeout) {
        clearTimeout(this.scanTimeout);
      }
    }
  }
};
</script>

<style scoped>
/* El input está oculto por CSS inline, pero por si acaso */
.scanner-input {
  position: absolute !important;
  left: -9999px !important;
  opacity: 0 !important;
  z-index: -1 !important;
}
</style>
